import React from 'react';
import { toast } from 'sonner';
import { Pencil, ArrowUp, ArrowDown, Trash2, Save, Loader2, Play, Pause, MoreVertical, Check, X, Copy, Lock, Unlock, Eye, Crosshair, Camera, Volume2 } from 'lucide-react';
import type { EcadAutomacaoPasso } from './types';
import { api } from '../../../api/client';

interface EcadPassoCardProps {
  passo: EcadAutomacaoPasso;
  index: number;
  selectedSubtopicoId: string | null;
  selectedStepIndexes: number[];
  onToggleSelectStep: (index: number) => void;
  handleUpdatePassoField: (index: number, field: keyof EcadAutomacaoPasso, value: any) => void;
  handleRemovePasso: (index: number) => void;
  handleMovePasso: (index: number, direction: 'up' | 'down') => void;
  handleSavePassos: (passosParaSalvar?: EcadAutomacaoPasso[], silent?: boolean) => Promise<void>;
  onRunFromStep: (ordem: number) => void;
  setZoomImage: (zoom: { stepIndex: number; type: 'real' | 'foco' } | null) => void;
  onActiveEditingIndex: (index: number | null) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isNewStep: boolean;
  setIsNewStep: (newStep: boolean) => void;
  passosLength: number;
  savingPassos: boolean;
  uiMap: Record<string, Record<string, { seletor: string; descricao: string }>>;
}

export const EcadPassoCard: React.FC<EcadPassoCardProps> = ({
  passo,
  index,
  selectedSubtopicoId,
  selectedStepIndexes,
  onToggleSelectStep,
  handleUpdatePassoField,
  handleRemovePasso,
  handleMovePasso,
  handleSavePassos,
  onRunFromStep,
  setZoomImage,
  onActiveEditingIndex,
  isEditing,
  setIsEditing,
  isNewStep,
  setIsNewStep,
  passosLength,
  savingPassos,
  uiMap
}) => {
  const [backupStep, setBackupStep] = React.useState<EcadAutomacaoPasso | null>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isSynthesizing, setIsSynthesizing] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handleStartEdit = () => {
    setBackupStep({ ...passo });
    setIsEditing(true);
    onActiveEditingIndex(index);
  };

  const handleCancelEdit = () => {
    onActiveEditingIndex(null);
    if (isNewStep) {
      handleRemovePasso(index);
      setIsNewStep(false);
      setIsEditing(false);
    } else {
      if (backupStep) {
        handleUpdatePassoField(index, 'tipoAcao', backupStep.tipoAcao);
        handleUpdatePassoField(index, 'selectorCss', backupStep.selectorCss);
        handleUpdatePassoField(index, 'valor', backupStep.valor || '');
        handleUpdatePassoField(index, 'legenda', backupStep.legenda || '');
        handleUpdatePassoField(index, 'urlAtual', backupStep.urlAtual || null);
        handleUpdatePassoField(index, 'autenticacaoNecessaria', backupStep.autenticacaoNecessaria || false);
      }
      setIsEditing(false);
      setBackupStep(null);
    }
  };

  const handleSaveStep = async () => {
    onActiveEditingIndex(null);
    await handleSavePassos();
    setIsEditing(false);
    setIsNewStep(false);
    setBackupStep(null);
  };

  const handlePlayPause = () => {
    if (!passo.audioUrl) return;

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(e => console.error(e));
        setIsPlaying(true);
      }
    } else {
      const audio = new Audio(passo.audioUrl);
      audioRef.current = audio;
      setIsPlaying(true);
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
      });
      
      audio.play().catch(e => {
        console.error(e);
        setIsPlaying(false);
      });
    }
  };

  const handleRegenerateVoice = async () => {
    if (!passo.legenda) return;
    setIsSynthesizing(true);
    try {
      const res = await api.post('/ecad/sintetizar', { text: passo.legenda, force: true });
      const data = res.data;
      if (data.ok && data.audioUrl) {
        handleUpdatePassoField(index, 'audioUrl', data.audioUrl);
        // Reset local audio ref so next play instantiates new source
        audioRef.current = null;
      } else {
        toast.error('Falha ao sintetizar áudio: ' + (data.message || 'Erro desconhecido'));
      }
    } catch (err: any) {
      console.error(err);
      toast.error('Erro ao conectar com o servidor para sintetizar.');
    } finally {
      setIsSynthesizing(null as any);
      setMenuOpen(false);
    }
  };

  const handleDownloadVoice = () => {
    if (!passo.audioUrl) return;
    const link = document.createElement('a');
    link.href = passo.audioUrl;
    link.download = `alice_passo_${index + 1}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setMenuOpen(false);
  };

  // Screenshots agora vêm exclusivamente de passo.imagemReal (base64 do dry-run/simulação)

  return (
    <div 
      id={`step-card-${index}`}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' || 
          (target.tagName === 'TEXTAREA' && isEditing) || 
          target.tagName === 'SELECT' || 
          target.tagName === 'BUTTON' || 
          target.closest('button') || 
          target.closest('a') ||
          target.closest('select') ||
          target.closest('input') ||
          (target.closest('textarea') && isEditing)
        ) {
          return;
        }
        if (!isEditing) {
          onToggleSelectStep(index);
        }
      }}
      style={{
        background: '#ffffff',
        border: selectedStepIndexes.includes(index) ? '1px solid #7c3aed' : '1px solid #e2e8f0',
        boxShadow: selectedStepIndexes.includes(index) ? '0 4px 12px rgba(124, 58, 237, 0.08)' : '0 1px 3px rgba(0,0,0,0.02)',
        borderRadius: '10px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        transition: 'all 0.2s',
        cursor: isEditing ? 'default' : 'pointer'
      }}
    >
      {isEditing ? (
        /* EDIT MODE — 3-ZONE LAYOUT */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>

          {/* ═══ ZONA 1: Barra Superior ═══ */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', width: '100%' }}>
            <input 
              type="checkbox"
              checked={selectedStepIndexes.includes(index)}
              onChange={() => onToggleSelectStep(index)}
              style={{
                width: '15px',
                height: '15px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                cursor: 'pointer',
                accentColor: '#8b5cf6',
                flexShrink: 0
              }}
            />
            <span style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: passo.dryRunStatus === 'PASS' ? '#ecfdf5' : passo.dryRunStatus === 'FAIL' ? '#fef2f2' : '#f5f3ff',
              border: passo.dryRunStatus === 'PASS' ? '1px solid #a7f3d0' : passo.dryRunStatus === 'FAIL' ? '1px solid #fecaca' : '1px solid #ddd6fe',
              color: passo.dryRunStatus === 'PASS' ? '#047857' : passo.dryRunStatus === 'FAIL' ? '#b91c1c' : '#6d28d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 800,
              flexShrink: 0
            }}>
              {index + 1}
            </span>

            {/* Action Type Dropdown */}
            <select
              value={passo.tipoAcao}
              onChange={(e) => handleUpdatePassoField(index, 'tipoAcao', e.target.value)}
              style={{
                padding: '5px 8px',
                borderRadius: '6px',
                border: '1px solid #ddd6fe',
                background: '#f5f3ff',
                color: '#6d28d9',
                fontSize: '0.72rem',
                outline: 'none',
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              <option value="EXPLICAR">EXPLICAR</option>
              <option value="DESTACAR">DESTACAR</option>
              <option value="ROLAR_PARA">ROLAR_PARA</option>
              <option value="ROLAR_TOPO">ROLAR_TOPO</option>
              <option value="MOVER_E_CLICAR">MOVER_E_CLICAR</option>
              <option value="DIGITAR">DIGITAR</option>
              <option value="ESPERAR_TEMPO">ESPERAR_TEMPO</option>
              <option value="ESPERAR_PAGINA">ESPERAR_PAGINA</option>
              <option value="RESOLVER_CAPTCHA">RESOLVER_CAPTCHA</option>
            </select>

            {/* Smart Selector: Two Dropdowns */}
            {!['ESPERAR_TEMPO', 'ESPERAR_PAGINA', 'ROLAR_TOPO'].includes(passo.tipoAcao) && (() => {
              const parts = (passo.selectorCss || '').split('.');
              const currentPage = parts.length >= 2 ? parts[0] : '';
              const currentElement = parts.length >= 2 ? parts.slice(1).join('.') : '';
              const pageKeys = Object.keys(uiMap).filter(k => k.length > 0).sort();
              const elements = currentPage && uiMap[currentPage] ? uiMap[currentPage] : {};
              const elementKeys = Object.keys(elements).sort();

              const getTypeIcon = (key: string) => {
                if (key.startsWith('botao_')) return '🟢';
                if (key.startsWith('input_')) return '🔤';
                if (key.startsWith('titulo_')) return '📰';
                if (key.startsWith('secao_')) return '📦';
                if (key.startsWith('card_')) return '🃏';
                if (key.startsWith('label_')) return '🏷️';
                return '🎯';
              };

              return (
                <div style={{ display: 'flex', gap: '4px', flex: 1, minWidth: '180px' }}>
                  <select
                    value={currentPage}
                    onChange={(e) => {
                      const newPage = e.target.value;
                      handleUpdatePassoField(index, 'selectorCss', newPage ? newPage + '.' : '');
                    }}
                    style={{
                      width: '130px',
                      padding: '5px 6px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#334155',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      outline: 'none',
                      flexShrink: 0
                    }}
                  >
                    <option value="">📄 Página...</option>
                    {pageKeys.map(pk => (
                      <option key={pk} value={pk}>{pk.replace(/_/g, ' ')}</option>
                    ))}
                  </select>

                  <select
                    value={currentElement}
                    onChange={(e) => {
                      const newElement = e.target.value;
                      if (currentPage && newElement) {
                        handleUpdatePassoField(index, 'selectorCss', currentPage + '.' + newElement);
                      }
                    }}
                    disabled={!currentPage}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '5px 6px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      background: currentPage ? '#ffffff' : '#f1f5f9',
                      color: currentPage ? '#334155' : '#94a3b8',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      outline: 'none'
                    }}
                  >
                    <option value="">🎯 Elemento...</option>
                    {elementKeys.map(ek => (
                      <option key={ek} value={ek}>
                        {getTypeIcon(ek)} {ek.replace(/_/g, ' ')} — {elements[ek]?.descricao?.replace('[Auto-Detectado] ', '').substring(0, 40)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {/* Login Toggle */}
            <button
              onClick={() => handleUpdatePassoField(index, 'autenticacaoNecessaria', !passo.autenticacaoNecessaria)}
              title={passo.autenticacaoNecessaria ? 'Requer login (clique para desativar)' : 'Não requer login (clique para ativar)'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: passo.autenticacaoNecessaria ? '1px solid #fecaca' : '1px solid #e2e8f0',
                background: passo.autenticacaoNecessaria ? '#fef2f2' : '#f8fafc',
                color: passo.autenticacaoNecessaria ? '#ef4444' : '#64748b',
                cursor: 'pointer',
                fontSize: '0.65rem',
                fontWeight: 700,
                flexShrink: 0,
                transition: 'all 0.2s'
              }}
            >
              {passo.autenticacaoNecessaria ? <Lock size={11} /> : <Unlock size={11} />}
            </button>

            {/* Save & Cancel */}
            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto', flexShrink: 0 }}>
              <button
                onClick={handleSaveStep}
                disabled={savingPassos}
                style={{
                  background: '#7c3aed',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  fontSize: '0.72rem',
                  color: '#ffffff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  transition: 'all 0.2s'
                }}
              >
                {savingPassos ? <Loader2 size={11} className="spin" /> : <Check size={11} />}
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  fontSize: '0.72rem',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  transition: 'all 0.2s'
                }}
              >
                <X size={11} />
              </button>
            </div>
          </div>

          {/* ═══ ZONA 2: Corpo Split (60/40) ═══ */}
          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>

            {/* Coluna Esquerda (60%) */}
            <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>

              {/* Valor / Conteúdo (adapta ao tipoAcao) */}
              {['DIGITAR', 'ESPERAR_TEMPO', 'ESPERAR_PAGINA', 'RESOLVER_CAPTCHA'].includes(passo.tipoAcao) && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {passo.tipoAcao === 'DIGITAR' ? '⌨️ Texto a Digitar' : passo.tipoAcao === 'ESPERAR_TEMPO' ? '⏱️ Tempo (ms)' : passo.tipoAcao === 'ESPERAR_PAGINA' ? '🌐 URL para Aguardar' : '🤖 Captcha'}
                  </label>
                  <input 
                    type={passo.tipoAcao === 'ESPERAR_TEMPO' ? 'number' : 'text'}
                    placeholder={
                      passo.tipoAcao === 'DIGITAR'
                        ? 'Ex: admin@alunoconectado.com.br'
                        : passo.tipoAcao === 'ESPERAR_TEMPO'
                        ? 'Ex: 4000'
                        : passo.tipoAcao === 'ESPERAR_PAGINA'
                        ? 'Ex: /dashboard'
                        : 'Campo Resposta'
                    }
                    value={passo.valor || ''}
                    onChange={(e) => handleUpdatePassoField(index, 'valor', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#334155',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      outline: 'none',
                      boxSizing: 'border-box' as any
                    }}
                  />
                  {passo.tipoAcao === 'ESPERAR_TEMPO' && passo.valor && (
                    <span style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '2px', display: 'block' }}>
                      = {(Number(passo.valor) / 1000).toFixed(1)} segundos
                    </span>
                  )}
                </div>
              )}

              {/* Narrativa da Alice */}
              <div>
                <label style={{ display: 'block', fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🎙️ Narrativa Alice
                </label>
                <textarea
                  placeholder="O que a Alice fala neste passo..."
                  value={passo.legenda || ''}
                  onChange={(e) => handleUpdatePassoField(index, 'legenda', e.target.value)}
                  rows={Math.max(2, Math.ceil((passo.legenda || '').length / 70))}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    fontSize: '0.78rem',
                    lineHeight: '1.45',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box' as any,
                    fontWeight: 500
                  }}
                />
              </div>

              {/* URL Atual (compacto) */}
              <div>
                <label style={{ display: 'block', fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🔗 URL Atual
                </label>
                <input 
                  type="text"
                  placeholder="/admin/escola (opcional)"
                  value={passo.urlAtual || ''}
                  onChange={(e) => handleUpdatePassoField(index, 'urlAtual', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    outline: 'none',
                    boxSizing: 'border-box' as any
                  }}
                />
              </div>
            </div>

            {/* Coluna Direita (40%) — Preview */}
            <div style={{ flex: '0 0 38%', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
              {/* Preview Screenshot */}
              <div style={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
                overflow: 'hidden',
                minHeight: '80px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {passo.imagemReal ? (
                  <img 
                    src={passo.imagemReal}
                    alt={`Passo ${index + 1}`}
                    style={{ width: '100%', height: 'auto', display: 'block', cursor: 'zoom-in' }}
                    onClick={() => setZoomImage({ stepIndex: index, type: 'real' })}
                  />
                ) : (
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#475569' }}>
                      <Camera size={18} />
                      <span style={{ fontSize: '0.62rem', fontWeight: 600 }}>Sem pré-visualização</span>
                      <span style={{ fontSize: '0.55rem', color: '#64748b' }}>Execute Dry-Run ou Simulação</span>
                    </div>
                  </div>
                )}
                {/* Dry-run status badge */}
                {passo.dryRunStatus && (
                  <span style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: '6px',
                    background: passo.dryRunStatus === 'PASS' ? 'rgba(16, 185, 129, 0.8)' : 'rgba(239, 68, 68, 0.8)',
                    color: '#fff'
                  }}>
                    {passo.dryRunStatus === 'PASS' ? '✅ OK' : '❌ FAIL'}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => setZoomImage({ stepIndex: index, type: 'real' })}
                  title="Ver screenshot"
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    padding: '4px',
                    borderRadius: '5px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  <Eye size={10} /> Ver
                </button>
                <button
                  onClick={() => setZoomImage({ stepIndex: index, type: 'foco' })}
                  title="Ver foco"
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    padding: '4px',
                    borderRadius: '5px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  <Crosshair size={10} /> Foco
                </button>
              </div>
            </div>
          </div>

          {/* ═══ ZONA 3: Barra Inferior (Metadados + Ações) ═══ */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            paddingTop: '6px',
            borderTop: '1px solid #f1f5f9'
          }}>
            {/* Metadados */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              {passo.tempoInicio && passo.tempoFim && (
                <span style={{ fontSize: '0.62rem', color: '#6d28d9', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '2px 6px', borderRadius: '10px', fontWeight: 600 }}>
                  ⏱️ {passo.tempoInicio} - {passo.tempoFim}
                </span>
              )}
              {passo.audioUrl && (
                <span style={{ fontSize: '0.62rem', color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '2px 6px', borderRadius: '10px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Volume2 size={9} /> Áudio
                </span>
              )}
              {passo.dryRunStatus && (
                <span style={{ 
                  fontSize: '0.62rem', 
                  fontWeight: 700,
                  color: passo.dryRunStatus === 'PASS' ? '#047857' : '#b91c1c',
                  background: passo.dryRunStatus === 'PASS' ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${passo.dryRunStatus === 'PASS' ? '#a7f3d0' : '#fecaca'}`,
                  padding: '2px 6px', 
                  borderRadius: '10px'
                }}>
                  {passo.dryRunStatus === 'PASS' ? '✅' : '❌'} {passo.dryRunStatus}
                </span>
              )}
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
              <button
                onClick={() => handleMovePasso(index, 'up')}
                disabled={index === 0}
                title="Mover para cima"
                style={{
                  padding: '3px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  color: index === 0 ? '#cbd5e1' : '#64748b',
                  cursor: index === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex'
                }}
              >
                <ArrowUp size={11} />
              </button>
              <button
                onClick={() => handleMovePasso(index, 'down')}
                disabled={index === passosLength - 1}
                title="Mover para baixo"
                style={{
                  padding: '3px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  color: index === passosLength - 1 ? '#cbd5e1' : '#64748b',
                  cursor: index === passosLength - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex'
                }}
              >
                <ArrowDown size={11} />
              </button>
              <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 2px' }} />
              <button
                onClick={() => handleRemovePasso(index)}
                title="Remover passo"
                style={{
                  padding: '3px',
                  borderRadius: '4px',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  color: '#ef4444',
                  cursor: 'pointer',
                  display: 'flex'
                }}
              >
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* READ MODE ROW */
        <>
          {/* Header Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input 
                type="checkbox"
                checked={selectedStepIndexes.includes(index)}
                onChange={() => onToggleSelectStep(index)}
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  cursor: 'pointer',
                  accentColor: '#8b5cf6'
                }}
              />
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: passo.dryRunStatus === 'PASS' ? '#ecfdf5' : passo.dryRunStatus === 'FAIL' ? '#fef2f2' : '#f5f3ff',
                border: passo.dryRunStatus === 'PASS' ? '1px solid #a7f3d0' : passo.dryRunStatus === 'FAIL' ? '1px solid #fecaca' : '1px solid #ddd6fe',
                color: passo.dryRunStatus === 'PASS' ? '#047857' : passo.dryRunStatus === 'FAIL' ? '#b91c1c' : '#6d28d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 800,
                transition: 'all 0.3s'
              }}>
                {index + 1}
              </span>
              <span style={{ 
                fontSize: '0.75rem', 
                fontWeight: 800, 
                color: '#6d28d9', 
                background: '#f5f3ff', 
                border: '1px solid #ddd6fe',
                padding: '2px 8px', 
                borderRadius: '6px' 
              }}>
                {passo.tipoAcao}
              </span>
              {passo.tempoInicio && passo.tempoFim && (
                <span style={{ fontSize: '0.72rem', color: '#6d28d9', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                  ⏱️ {passo.tempoInicio} - {passo.tempoFim}
                </span>
              )}
              {passo.urlAtual && (
                <span style={{ fontSize: '0.72rem', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: '12px', fontWeight: 500 }}>
                  🌐 {passo.urlAtual}
                </span>
              )}
              {passo.autenticacaoNecessaria && (
                <span style={{ fontSize: '0.72rem', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  🔒 Requer Login
                </span>
              )}
              {passo.selectorCss && (() => {
                const parts = passo.selectorCss.split('.');
                const pk = parts.length >= 2 ? parts[0] : '';
                const ek = parts.length >= 2 ? parts.slice(1).join('.') : passo.selectorCss;
                const icon = ek.startsWith('botao_') ? '🟢' : ek.startsWith('input_') ? '🔤' : ek.startsWith('titulo_') ? '📰' : ek.startsWith('secao_') ? '📦' : ek.startsWith('card_') ? '🃏' : '🎯';
                return (
                  <span style={{ fontSize: '0.72rem', color: '#6d28d9', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '2px 8px', borderRadius: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {icon} {pk && <span style={{ color: '#64748b' }}>{pk.replace(/_/g, ' ')} →</span>} {ek.replace(/_/g, ' ')}
                  </span>
                );
              })()}
              {passo.valor && (
                <span style={{ fontSize: '0.75rem', color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '2px 6px', borderRadius: '4px' }}>
                  Valor: "{passo.valor}"
                </span>
              )}
              {passo.dryRunStatus && (
                <span 
                  style={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 700,
                    color: passo.dryRunStatus === 'PASS' ? '#047857' : passo.dryRunStatus === 'FAIL' ? '#b91c1c' : '#475569', 
                    background: passo.dryRunStatus === 'PASS' ? '#ecfdf5' : passo.dryRunStatus === 'FAIL' ? '#fef2f2' : '#f1f5f9', 
                    border: `1px solid ${passo.dryRunStatus === 'PASS' ? '#a7f3d0' : passo.dryRunStatus === 'FAIL' ? '#fecaca' : '#cbd5e1'}`, 
                    padding: '2px 6px', 
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title={passo.dryRunMotivo || ''}
                >
                  {passo.dryRunStatus === 'PASS' ? '✅' : passo.dryRunStatus === 'FAIL' ? '❌' : '⏭️'}
                </span>
              )}
            </div>

            {/* Controls (Play, Edit, and Dropdown) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Play/Pause Button */}
              {passo.audioUrl && (
                <button
                  onClick={handlePlayPause}
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: isPlaying ? '#fef2f2' : '#ecfdf5',
                    border: isPlaying ? '1px solid #ef4444' : '1px solid #10b981',
                    color: isPlaying ? '#dc2626' : '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  title={isPlaying ? 'Pausar' : 'Ouvir Narração'}
                >
                  {isPlaying ? (
                    <Pause size={12} />
                  ) : (
                    <Play size={12} fill="currentColor" style={{ marginLeft: '1px' }} />
                  )}
                </button>
              )}

              {/* "Editar" Pencil button */}
              <button
                onClick={handleStartEdit}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '6px',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.2s'
                }}
                title="Editar Passo"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f3ff';
                  e.currentTarget.style.borderColor = '#7c3aed';
                  e.currentTarget.style.color = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                <Pencil size={12} />
              </button>

              {/* Three-dots dropdown menu */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '6px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <MoreVertical size={14} />
                </button>

                {menuOpen && (
                  <>
                    <div 
                      onClick={() => setMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'transparent' }} 
                    />
                    <div style={{
                      position: 'absolute',
                      top: '30px',
                      right: 0,
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -4px rgba(0,0,0,0.05)',
                      zIndex: 100,
                      minWidth: '150px',
                      overflow: 'hidden'
                    }}>
                      <button
                        onClick={() => {
                          onRunFromStep(passo.ordem);
                          setMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: '#7c3aed',
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 600
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Play size={11} fill="currentColor" style={{ color: '#c084fc' }} /> Executar Deste Passo
                      </button>

                      <button
                        onClick={() => {
                          handleStartEdit();
                          setMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: '#334155',
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Pencil size={11} /> Editar Passo
                      </button>

                      <button
                        onClick={() => {
                          handleMovePasso(index, 'up');
                          setMenuOpen(false);
                        }}
                        disabled={index === 0}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: index === 0 ? '#cbd5e1' : '#334155',
                          fontSize: '0.72rem',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => { if (index !== 0) e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <ArrowUp size={11} /> Subir Passo
                      </button>

                      <button
                        onClick={() => {
                          handleMovePasso(index, 'down');
                          setMenuOpen(false);
                        }}
                        disabled={index === passosLength - 1}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: index === passosLength - 1 ? '#cbd5e1' : '#334155',
                          fontSize: '0.72rem',
                          cursor: index === passosLength - 1 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => { if (index !== passosLength - 1) e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <ArrowDown size={11} /> Descer Passo
                      </button>

                      <button
                        onClick={handleRegenerateVoice}
                        disabled={isSynthesizing || !passo.legenda}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: !passo.legenda ? '#cbd5e1' : '#334155',
                          fontSize: '0.72rem',
                          cursor: !passo.legenda ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => { if (passo.legenda) e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        {isSynthesizing ? (
                          <Loader2 size={11} className="spin" />
                        ) : (
                          <Loader2 size={11} />
                        )}
                        {passo.audioUrl ? 'Regerar Voz' : 'Sintetizar Voz'}
                      </button>

                      {passo.audioUrl && (
                        <button
                          onClick={handleDownloadVoice}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            color: '#334155',
                            fontSize: '0.72rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <Save size={11} /> Baixar Áudio
                        </button>
                      )}

                      <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0' }} />

                      <button
                        onClick={() => {
                          handleRemovePasso(index);
                          setMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Trash2 size={11} /> Remover Passo
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Body Row */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
            {passo.legenda && (
              <textarea
                readOnly
                value={passo.legenda}
                rows={Math.max(2, Math.ceil(passo.legenda.length / 80))}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#334155',
                  fontSize: '0.8rem',
                  lineHeight: '1.45',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  display: 'block',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  pointerEvents: 'none'
                }}
              />
            )}

            {/* Screenshots thumbnails — só exibe quando tem screenshot capturada */}
            {passo.imagemReal && (
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '0' }}>
                <div 
                  style={{ position: 'relative', cursor: 'pointer', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomImage({ stepIndex: index, type: 'real' });
                  }}
                >
                  <img 
                    src={passo.imagemReal} 
                    alt={"Passo " + (index + 1) + " - Screenshot"} 
                    style={{ height: '36px', width: '60px', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Dry-Run Error Bar */}
          {passo.dryRunStatus === 'FAIL' && passo.dryRunMotivo && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '0.72rem',
              color: '#991b1b',
              lineHeight: 1.4
            }}>
              {passo.imagemReal && (
                <img 
                  src={passo.imagemReal} 
                  alt={`Dry-run passo ${index + 1}`}
                  style={{ 
                    width: '80px', 
                    height: '45px', 
                    objectFit: 'cover', 
                    borderRadius: '4px', 
                    border: '1px solid #fca5a5',
                    flexShrink: 0,
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomImage({ stepIndex: index, type: 'real' });
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '2px' }}>Falha no Dry-Run</div>
                <div style={{ color: '#7f1d1d', wordBreak: 'break-word' }}>{passo.dryRunMotivo}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
